import cookies from './cookies';
import { v4 as uuidv4 } from 'uuid';

/**
 * Promisify the XMLHttpRequest
 * @param {object} opts with following attributes:
 *      method: {String},
 *      url: {String},
 *      body: {Object},
 *      headers: {Object},
 *      timeout: {Number?} (in milliseconds),
 * @return {Promise} resolves to the response from http request
 */
function request(opts) {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({
          response: xhr.response,
          headers: xhr.getAllResponseHeaders(),
          status: xhr.status,
        });
      } else {
        reject(compileError(xhr, opts));
      }
    };
    xhr.onerror = () => {
      reject(compileError(xhr, opts));
    };
    xhr.ontimeout = () => {
      reject(compileError({ status: 0, statusText: 'Timeout' }, opts));
    };

    let url = opts.url;

    if (opts.timeout) {
      xhr.timeout = opts.timeout;
    }

    xhr.open(opts.method, url);

    if (opts.headers) {
      Object.keys(opts.headers).forEach((key) => {
        xhr.setRequestHeader(key, opts.headers[key]);
      });
    }

    xhr.send(opts.body);
  });
}

function compileError(xhr, opts) {
  return {
    status: xhr.status,
    statusText: xhr.statusText,
    config: { url: opts.url },
  };
}

function postJSON(url, body, timeout) {
  const transactionId = uuidv4();
  return request({
    method: 'POST',
    url: url,
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      'X-Transaction-ID': transactionId,
      'X-Request-ID': uuidv4(),
    },
    timeout,
  })
    .then(({ response }) => response)
    .then((data) => {
      try {
        return JSON.parse(data);
      } catch (e) {
        throw {
          statusText: e.toString(),
        };
      }
    })
    .catch((err) => {
      throw { ...err, transactionId };
    });
}

function getJSON(url, headers, timeout) {
  return request({
    method: 'GET',
    url: url,
    headers: headers ?? undefined,
    timeout,
  })
    .then(({ response }) => response)
    .then((data) => {
      try {
        return JSON.parse(data);
      } catch (e) {
        throw {
          statusText: e.toString(),
        };
      }
    });
}

function getWithCsrf(url) {
  return request({
    method: 'GET',
    url: url,
    headers: {
      'X-XSRF-TOKEN': cookies.get('XSRF-TOKEN'),
    },
  }).then(({ response }) => response);
}

function patchJSON(url, body, timeout) {
  const transactionId = uuidv4();
  return request({
    method: 'PATCH',
    url: url,
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      'X-Transaction-ID': transactionId,
      'X-Request-ID': uuidv4(),
      'X-XSRF-TOKEN': cookies.get('XSRF-TOKEN'),
    },
    timeout,
  })
    .then(({ response }) => response)
    .then((data) => {
      if (!data) return null;
      try {
        return JSON.parse(data);
      } catch (e) {
        throw {
          statusText: e.toString(),
        };
      }
    })
    .catch((err) => {
      throw { ...err, transactionId };
    });
}

const exports = {
  getJSON,
  postJSON,
  patchJSON,
  getWithCsrf,
  request,
};

export default exports;
